/**
 * Cochran's Q test for k related binary outcomes — the repeated-measures analogue of the
 * chi-square test, and the k-condition extension of McNemar's test.
 *
 * With n subjects, k conditions coded 0/1, column totals Cⱼ, row totals Rᵢ and grand total T:
 *
 *   Q = (k − 1) · [k · ΣCⱼ² − T²] / [k · T − ΣRᵢ²]
 *
 * referred to χ² with k − 1 degrees of freedom. A subject whose k outcomes are all 0 or all 1
 * contributes nothing to Q and is called uninformative. Q equals the tie-corrected Friedman
 * statistic on the 0/1 table (checked against R's `friedman.test` in the tests), so
 * Kendall's W = Q / (n(k − 1)) is a natural effect size.
 *
 * Follow-up: every pair of conditions is compared by McNemar's test on the discordant
 * pairs (b: yes → no, c: no → yes). The exact two-sided p-value is Binomial(b + c, ½); it is
 * adjusted for the k(k − 1)/2 comparisons by Bonferroni and Holm.
 *
 * Cochran (1950) suggests the χ² approximation is dependable when n ≥ 4 informative subjects
 * and n·k ≥ 24.
 */

import { chiSquareUpper } from "@/lib/stats/distributions";
import { mcnemar } from "@/lib/tool-kit/stats-tests/mcnemar";
import {
  bonferroniAdjust,
  holmAdjust,
} from "@/lib/tool-kit/stats-tests-more/adjust";

export interface CochranPair {
  first: number;
  second: number;
  /** Yes in both, yes then no, no then yes, no in both. */
  a: number;
  b: number;
  c: number;
  d: number;
  /** Exact two-sided McNemar p-value (1 when there are no discordant pairs). */
  pExact: number;
  pBonferroni: number;
  pHolm: number;
  /** McNemar χ² with Edwards's continuity correction, and its p-value. */
  chiSquareCorrected: number;
  pChiSquare: number;
}

export interface CochranQResult {
  n: number;
  k: number;
  /** Subjects whose outcomes are not all the same. */
  informative: number;
  columnTotals: number[];
  rowTotals: number[];
  grandTotal: number;
  proportions: number[];
  q: number;
  df: number;
  p: number;
  kendallW: number;
  pairs: CochranPair[];
  /** True when n ≥ 4 informative subjects and n·k ≥ 24 (Cochran's guideline). */
  approximationOk: boolean;
}

export type CochranFailure =
  | "too-few-subjects"
  | "too-few-conditions"
  | "not-binary"
  | "no-variation";

export type CochranOutcome =
  | { ok: true; result: CochranQResult }
  | { ok: false; reason: CochranFailure };

/** The first cell that is neither 0 nor 1, as {row, column} (1-based), or null. */
export function firstNonBinary(
  rows: readonly (readonly number[])[]
): { row: number; column: number; value: number } | null {
  for (const [r, row] of rows.entries()) {
    for (const [c, value] of row.entries()) {
      if (value !== 0 && value !== 1) {
        return { row: r + 1, column: c + 1, value };
      }
    }
  }
  return null;
}

function pairTable(
  rows: readonly (readonly number[])[],
  first: number,
  second: number
): { a: number; b: number; c: number; d: number } {
  let a = 0;
  let b = 0;
  let c = 0;
  let d = 0;
  for (const row of rows) {
    if (row[first] === 1 && row[second] === 1) {
      a += 1;
    } else if (row[first] === 1) {
      b += 1;
    } else if (row[second] === 1) {
      c += 1;
    } else {
      d += 1;
    }
  }
  return { a, b, c, d };
}

function pairwise(
  rows: readonly (readonly number[])[],
  k: number
): CochranPair[] {
  const pairs: CochranPair[] = [];
  for (let i = 0; i < k; i++) {
    for (let j = i + 1; j < k; j++) {
      const cells = pairTable(rows, i, j);
      const test = mcnemar(cells.a, cells.b, cells.c, cells.d);
      pairs.push({
        first: i,
        second: j,
        ...cells,
        pExact: test ? test.pExact : 1,
        pBonferroni: 0,
        pHolm: 0,
        chiSquareCorrected: test ? test.chiSquareCorrected : 0,
        pChiSquare: test ? test.pChiSquareCorrected : 1,
      });
    }
  }
  const raw = pairs.map((pair) => pair.pExact);
  const bonferroni = bonferroniAdjust(raw);
  const holm = holmAdjust(raw);
  for (const [index, pair] of pairs.entries()) {
    pair.pBonferroni = bonferroni[index];
    pair.pHolm = holm[index];
  }
  return pairs;
}

/** `rows` has one array of 0/1 values per subject. */
export function cochransQ(
  rows: readonly (readonly number[])[]
): CochranOutcome {
  const n = rows.length;
  const k = rows[0]?.length ?? 0;
  if (n < 2) {
    return { ok: false, reason: "too-few-subjects" };
  }
  if (k < 2) {
    return { ok: false, reason: "too-few-conditions" };
  }
  if (firstNonBinary(rows)) {
    return { ok: false, reason: "not-binary" };
  }
  const columnTotals = Array.from({ length: k }, (_, j) =>
    rows.reduce((acc, row) => acc + row[j], 0)
  );
  const rowTotals = rows.map((row) => row.reduce((a, b) => a + b, 0));
  const grandTotal = columnTotals.reduce((a, b) => a + b, 0);
  const sumRowSquares = rowTotals.reduce((acc, r) => acc + r * r, 0);
  const denominator = k * grandTotal - sumRowSquares;
  if (denominator === 0) {
    return { ok: false, reason: "no-variation" };
  }
  const sumColumnSquares = columnTotals.reduce((acc, c) => acc + c * c, 0);
  const q = ((k - 1) * (k * sumColumnSquares - grandTotal ** 2)) / denominator;
  const df = k - 1;
  const informative = rowTotals.filter((r) => r > 0 && r < k).length;
  return {
    ok: true,
    result: {
      n,
      k,
      informative,
      columnTotals,
      rowTotals,
      grandTotal,
      proportions: columnTotals.map((c) => c / n),
      q,
      df,
      p: chiSquareUpper(q, df),
      kendallW: q / (n * df),
      pairs: pairwise(rows, k),
      approximationOk: informative >= 4 && n * k >= 24,
    },
  };
}
